import { supabase } from './supabase';
import { getPublicUrl, listAllFilesRecursive, STORAGE_BUCKETS } from './storage';
import { loadStudioSettings, STUDIO_TEAL } from './settings';

export const INK = '#111111';
export const INK_RGB: [number, number, number] = [17, 17, 17];
export const STUDIO_SALES_EMAIL = 'sales@on45d.studio';

export type StudioBranding = {
  studioName: string;
  legalName: string;
  contactEmail: string;
  phone: string;
  addressLine1: string;
  city: string;
  region: string;
  postalCode: string;
  country: string;
  taxId: string;
  taxRate: number;
  currency: string;
  primaryColor: string;
  accentColor: string;
  secondaryColor: string;
  logoUrl?: string;
};

export function studioAddressLines(
  branding: Pick<StudioBranding, 'addressLine1' | 'city' | 'region' | 'postalCode' | 'country'>
) {
  const cityState = [branding.city, [branding.region, branding.postalCode].filter(Boolean).join(' ')]
    .filter(Boolean)
    .join(', ');
  return [branding.addressLine1, cityState, branding.country].filter((line) => Boolean(line && line.trim()));
}

export function hexToRgb(hex: string): [number, number, number] {
  const raw = String(hex || '').replace('#', '').trim();
  const full = raw.length === 3 ? raw.split('').map((char) => char + char).join('') : raw;
  if (!/^[0-9a-fA-F]{6}$/.test(full)) {
    return [27, 122, 116];
  }
  const n = parseInt(full, 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

export function mixWithWhite(rgb: [number, number, number], amount = 0.82): [number, number, number] {
  const t = Math.min(1, Math.max(0, amount));
  return [
    Math.round(rgb[0] + (255 - rgb[0]) * t),
    Math.round(rgb[1] + (255 - rgb[1]) * t),
    Math.round(rgb[2] + (255 - rgb[2]) * t),
  ];
}

function readLogoUrl(content: unknown): string | undefined {
  if (!content || typeof content !== 'object') return undefined;
  const logo = (content as { logo?: unknown }).logo;
  if (typeof logo === 'string' && logo.trim()) return logo.trim();
  if (logo && typeof logo === 'object') {
    const record = logo as { en?: unknown; es?: unknown };
    const nested = [record.en, record.es].find((value) => typeof value === 'string' && value.trim());
    if (typeof nested === 'string') return nested.trim();
  }
  return undefined;
}

async function findLogoFromContent(): Promise<string | undefined> {
  const { data, error } = await supabase.from('website_content').select('section, content');
  if (error || !data) return undefined;
  const rows = Array.isArray(data) ? data : [data];
  for (const section of ['header', 'footer']) {
    const row = rows.find((item) => item?.section === section);
    const url = readLogoUrl(row?.content);
    if (url) return url;
  }
  return undefined;
}

async function findLogoFromStorage(): Promise<string | undefined> {
  const files = await listAllFilesRecursive(STORAGE_BUCKETS.LOGOS, '');
  const images = files
    .filter((file) => file?.id && /\.(png|jpe?g|gif|webp|svg)$/i.test(String(file.name || '')))
    .sort((a, b) => String(b.updated_at || b.created_at || '').localeCompare(String(a.updated_at || a.created_at || '')));
  const preferred =
    images.find((file) => String(file.fullPath || file.name || '').includes('header')) || images[0];
  if (!preferred) return undefined;
  const path = preferred.fullPath || preferred.name;
  return path ? getPublicUrl(STORAGE_BUCKETS.LOGOS, path) : undefined;
}

export async function loadStudioBranding(): Promise<StudioBranding> {
  const settings = await loadStudioSettings();
  let logoUrl: string | undefined;
  try {
    logoUrl = (await findLogoFromContent()) || (await findLogoFromStorage());
  } catch {
    logoUrl = undefined;
  }
  return {
    studioName: settings.studioName || 'ON45D Studio',
    legalName: settings.legalName || 'ON45D Studio LLC',
    contactEmail: STUDIO_SALES_EMAIL,
    phone: settings.phone || '+18137120063',
    addressLine1: settings.addressLine1 || '6402 Crystal Brook Drive',
    city: settings.city || 'Tampa',
    region: settings.region || 'FL',
    postalCode: settings.postalCode || '33625',
    country: settings.country || 'US',
    taxId: settings.taxId || '42-1827199',
    taxRate: Number(settings.taxRate || 0),
    currency: settings.currency || 'USD',
    primaryColor: settings.primaryColor || STUDIO_TEAL,
    accentColor: settings.accentColor || STUDIO_TEAL,
    secondaryColor: settings.secondaryColor || '#0b0f14',
    logoUrl,
  };
}
